import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInventory, useAnimals, scanChipLocal, finishInventoryLocal, cancelInventoryLocal } from "@/lib/repo";
import { ScanCTA } from "@/components/ScanCTA";
import { UnknownChipDialog } from "@/components/UnknownChipDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Search, CheckCircle2, HelpCircle, Save, Pen, AlertTriangle, Sparkles, ClipboardList, Clipboard } from "lucide-react";
import { toast } from "sonner";
import { AnimalStatusChip } from "@/components/AnimalStatusChip";
import { getRFIDReader } from "@/lib/rfid/IRFIDReader";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { parseChipTokens, summarizeParse, readClipboardText } from "@/lib/chipParse";

const FILTERS = [
  { key: "pendientes",   label: "Pendientes"   },
  { key: "presentes",    label: "Presentes"    },
  { key: "desconocidos", label: "Desconocidos" },
];

export default function InventorySession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const inv = useInventory(id);
  const allAnimals = useAnimals({ farm_id: inv?.farm_id, lot_id: inv?.lot_id || undefined });
  const expectedSet = useMemo(() => new Set(inv?.expected_animal_ids || []), [inv]);
  const animals = useMemo(() => allAnimals.filter((a) => expectedSet.has(a.id)), [allAnimals, expectedSet]);
  const scanned = useMemo(() => new Set(inv?.scanned_animal_ids || []), [inv]);
  const unknown = useMemo(() => inv?.unknown_chips || [], [inv]);
  const secondaryScans = useMemo(() => new Set(inv?.scans_via_secondary || []), [inv]);

  const [lastScan, setLastScan] = useState(null);
  const [filter, setFilter] = useState("pendientes");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [unknownDialogChip, setUnknownDialogChip] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [listText, setListText] = useState("");
  const [listBusy, setListBusy] = useState(false);
  const hudRef = useRef(null);

  const canEditChips = user?.role === "owner";

  useEffect(() => {
    if (!inv || animals.length === 0) return;
    (async () => {
      const reader = await getRFIDReader();
      if (reader.loadChipPool) {
        const allChips = animals.flatMap((a) => {
          const fromArray = (a.chips || []).map((c) => c.rfid).filter(Boolean);
          return fromArray.length ? fromArray : [a.chip_code].filter(Boolean);
        });
        reader.loadChipPool(allChips);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv?.id, animals.length]);

  const onChip = async (chip) => {
    if (!chip || busy) return;
    setBusy(true);
    try {
      const res = await scanChipLocal(id, chip);
      if (res.status === "present") {
        const animal = res.animal;
        const viaSecondary = !!res.via_secondary_chip;
        const chipStatusAtScan = res.chip_status_at_scan;
        setLastScan({
          status: "presente",
          chip,
          animal,
          via_secondary: viaSecondary,
          chip_status_at_scan: chipStatusAtScan,
        });
        if (chipStatusAtScan === "lost") {
          toast.success(`Chip ${chip} reapareció. Lectura registrada para Lomo ${animal.lomo_number || animal.id}.`);
        } else if (chipStatusAtScan === "damaged") {
          toast.info(`Chip secundario (marcado dañado) leído. Registrado como Lomo ${animal.lomo_number || animal.id}.`);
        } else if (viaSecondary) {
          toast.info(`Chip secundario leído. Mismo animal: Lomo ${animal.lomo_number || animal.id}.`);
        }
        if ("vibrate" in navigator) navigator.vibrate?.(40);
      } else {
        setLastScan({ status: "desconocido", chip });
        setUnknownDialogChip(chip);
        if ("vibrate" in navigator) navigator.vibrate?.([60, 40, 60]);
      }
      if (hudRef.current) {
        hudRef.current.classList.remove("scan-pulse");
        // eslint-disable-next-line no-unused-expressions
        hudRef.current.offsetWidth;
        hudRef.current.classList.add("scan-pulse");
      }
    } catch (e) {
      toast.error("No pude registrar el escaneo");
    } finally { setBusy(false); }
  };

  // Pegado masivo: procesa cada chip válido directamente contra Dexie (sin
  // abrir el diálogo de "chip desconocido" por cada uno) y muestra un único
  // resumen al final. Funciona offline igual que el escaneo uno por uno.
  const handleBulkPaste = async (text) => {
    const parsed = parseChipTokens(text);
    if (!parsed.valid.length && !parsed.invalid.length) { toast.error("Pegá o escribí una lista de chips"); return; }
    setListBusy(true);
    let present = 0, unknownCount = 0;
    try {
      for (const code of parsed.valid) {
        try {
          const res = await scanChipLocal(id, code);
          if (res.status === "present") present++; else unknownCount++;
        } catch (_) { /* seguir con el resto de la lista */ }
      }
      const parts = [`${present} presente${present === 1 ? "" : "s"}`];
      if (unknownCount) parts.push(`${unknownCount} desconocido${unknownCount === 1 ? "" : "s"}`);
      if (parsed.duplicates) parts.push(`${parsed.duplicates} repetido${parsed.duplicates === 1 ? "" : "s"} (se ignoran)`);
      if (parsed.invalid.length) parts.push(`${parsed.invalid.length} no válido${parsed.invalid.length === 1 ? "" : "s"}`);
      toast.success(`Lista cargada: ${parts.join(" · ")}`);
      setListText("");
      setListOpen(false);
    } finally { setListBusy(false); }
  };

  const pasteListFromClipboard = async () => {
    const text = await readClipboardText();
    if (text === null) { toast.error("No pude leer el portapapeles — pegá manualmente en el cuadro de texto"); return; }
    if (!text.trim()) { toast.info("El portapapeles está vacío"); return; }
    setListText(text);
  };

  const handleChipLinked = async (animalId) => {
    // El chip ahora está vinculado localmente (Dexie). Re-escaneamos para que
    // cuente en esta sesión de inventario.
    if (unknownDialogChip) {
      try { await scanChipLocal(id, unknownDialogChip); } catch (_) {}
    }
    setUnknownDialogChip(null);
  };

  const handleCreateNewFromChip = (chip) => {
    setUnknownDialogChip(null);
    navigate(`/animales/nuevo?chip=${encodeURIComponent(chip)}&farm_id=${inv?.farm_id || ""}`);
  };

  const finish = async () => {
    try {
      const finished = await finishInventoryLocal(id, null);
      toast.success(`Inventario cerrado: ${finished.scanned_animal_ids.length} presentes, ${finished.missing_animal_ids.length} faltantes`);
      navigate(`/inventario/${id}`);
    } catch (e) { toast.error("No pude cerrar el inventario"); }
  };

  const cancel = async () => {
    if (!window.confirm("¿Cancelar este inventario? No se guardará como cerrado.")) return;
    await cancelInventoryLocal(id);
    navigate("/inventario");
  };

  const counts = useMemo(() => ({
    expected: animals.length,
    present:  scanned.size,
    missing:  animals.length - scanned.size,
    unknown:  unknown.length,
  }), [animals.length, scanned, unknown]);

  const progress = counts.expected > 0 ? Math.min(100, Math.round((counts.present / counts.expected) * 100)) : 0;

  const filteredList = useMemo(() => {
    let l;
    if (filter === "presentes")    l = animals.filter((a) => scanned.has(a.id));
    else if (filter === "desconocidos") l = unknown.map((c) => ({ chip_code: c, _unknown: true }));
    else l = animals.filter((a) => !scanned.has(a.id));
    if (!search) return l;
    const q = search.toLowerCase();
    return l.filter((a) =>
      a.chip_code?.toLowerCase().includes(q) ||
      a.lomo_number?.toLowerCase()?.includes(q) ||
      a.ear_tattoo?.toLowerCase()?.includes(q)
    );
  }, [filter, animals, scanned, unknown, search]);

  if (!inv) return null;

  return (
    <div className="space-y-4 pb-44">
      {/* Compact title bar */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={()=>navigate("/inventario")} className="h-9 w-9"><ArrowLeft size={18} /></Button>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Sesión en curso</div>
          <h1 className="font-heading text-[20px] sm:text-[22px] font-semibold truncate tracking-tight">{inv.name || "Inventario"}</h1>
        </div>
        <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={cancel} data-testid="inventory-cancel-button">Cancelar</Button>
      </div>

      {/* Iconic Editorial counter */}
      <div
        ref={hudRef}
        className="rounded-[var(--radius)] border border-border bg-card p-5 sm:p-6 shadow-paper relative overflow-hidden"
      >
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Presentes</div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span data-testid="inventory-counts" className="font-editorial tabular-nums text-[72px] sm:text-[96px] leading-none tracking-tight text-primary">
                {counts.present}
              </span>
              <span className="font-mono text-muted-foreground tabular-nums text-base">
                / {counts.expected}
              </span>
            </div>
            <div className="mt-2 text-[12px] font-mono text-muted-foreground tabular-nums">
              {progress}% · {counts.missing} faltan{counts.missing === 1 ? "" : ""} · {counts.unknown} desconocido{counts.unknown === 1 ? "" : "s"}
            </div>
          </div>
          <div className="hidden sm:flex flex-col items-end gap-2">
            <div className="flex items-center gap-1.5">
              <Stat label="Faltan"  value={counts.missing} tone="missing" testId="inventory-stat-missing" />
              <Stat label="Descon." value={counts.unknown} tone="unknown" testId="inventory-stat-unknown" />
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-5 h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary transition-[width] duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        {/* Mobile stats */}
        <div className="mt-4 sm:hidden grid grid-cols-3 gap-2">
          <Stat label="Esperados" value={counts.expected} tone="neutral" />
          <Stat label="Faltan"    value={counts.missing} tone="missing" />
          <Stat label="Descon."   value={counts.unknown} tone="unknown" />
        </div>
      </div>

      {/* Last scan banner */}
      {lastScan && (
        <div
          data-testid="inventory-last-scan"
          className={cn(
            "rounded-[var(--radius)] border p-4 flex items-center justify-between gap-3 slide-down",
            lastScan.status === "presente"
              ? "bg-[hsl(var(--state-present-bg))] border-[hsl(var(--state-present)/0.35)]"
              : "bg-[hsl(var(--state-unknown-bg))] border-[hsl(var(--state-unknown)/0.35)]"
          )}
        >
          <div className="min-w-0">
            <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Último chip</div>
            <div className="font-mono text-[14px] tracking-[0.04em] tabular-nums text-foreground">{lastScan.chip}</div>
            {lastScan.animal && (
              <div className="text-[12px] text-foreground/80 mt-0.5">
                Lomo <span className="font-heading font-semibold">{lastScan.animal.lomo_number || "-"}</span> · {lastScan.animal.breed}
              </div>
            )}
            {lastScan.via_secondary && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <Badge variant="warm" data-testid="inventory-last-scan-secondary-badge">
                  <Sparkles size={10}/>Chip secundario
                </Badge>
                {lastScan.chip_status_at_scan === "lost" && (
                  <Badge variant="ochre"><AlertTriangle size={10}/>Reapareció (estaba perdido)</Badge>
                )}
                {lastScan.chip_status_at_scan === "damaged" && (
                  <Badge variant="ochre"><AlertTriangle size={10}/>Marcado dañado</Badge>
                )}
              </div>
            )}
          </div>
          {lastScan.status === "presente"
            ? <CheckCircle2 size={28} className="text-[hsl(var(--state-present))] shrink-0" />
            : <HelpCircle size={28} className="text-[hsl(var(--state-unknown))] shrink-0" />
          }
        </div>
      )}

      {secondaryScans.size > 0 && (
        <div
          data-testid="inventory-secondary-count-banner"
          className="rounded-[var(--radius)] border border-[hsl(var(--brand-warm)/0.35)] bg-[hsl(var(--brand-warm)/0.10)] px-4 py-2.5 text-[12.5px] text-foreground/85 flex items-center gap-2"
        >
          <Sparkles size={14} className="text-[hsl(var(--brand-warm))]"/>
          <span><span className="font-mono tabular-nums">{secondaryScans.size}</span> animal{secondaryScans.size === 1 ? "" : "es"} contado{secondaryScans.size === 1 ? "" : "s"} por chip secundario. Revisá el resumen al cerrar.</span>
        </div>
      )}

      {/* Filter pills + search */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={()=>setFilter(f.key)}
            data-testid={`inventory-filter-${f.key}`}
            className={cn(
              "px-3 h-9 rounded-full text-[12px] font-medium border transition-colors duration-150 ring-focus",
              filter === f.key
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card border-border text-foreground hover:bg-muted"
            )}
          >
            {f.label}
          </button>
        ))}
        <div className="relative flex-1 min-w-[140px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar…" className="h-9 pl-9 text-base sm:text-sm" data-testid="inventory-search-input" />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="divide-hairline max-h-[40vh] overflow-y-auto" data-testid="inventory-list">
            {filteredList.length === 0 && <div className="text-sm text-muted-foreground text-center py-10">Sin elementos en este filtro.</div>}
            {filteredList.map((a, idx) => {
              if (a._unknown) {
                return (
                  <div key={a.chip_code + idx} className="p-3 sm:p-4 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-mono text-[13px] tracking-[0.04em] tabular-nums text-foreground">{a.chip_code}</div>
                      <div className="text-[11.5px] text-muted-foreground mt-0.5">Chip no registrado</div>
                    </div>
                    <AnimalStatusChip status="desconocido" />
                  </div>
                );
              }
              const present = scanned.has(a.id);
              return (
                <div
                  key={a.id}
                  className={cn(
                    "p-3 sm:p-4 flex items-center justify-between gap-3 border-l-2 transition-colors duration-150",
                    present
                      ? "bg-[hsl(var(--state-present-bg)/0.5)] border-[hsl(var(--state-present))]"
                      : "border-transparent"
                  )}
                >
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-heading font-semibold text-foreground tracking-tight">Lomo {a.lomo_number || "—"}</div>
                    <div className="text-[11.5px] text-muted-foreground font-mono tabular-nums mt-0.5">{a.chip_code}</div>
                  </div>
                  <AnimalStatusChip status={present ? "presente" : "pendiente"} />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Floating action bar */}
      <div className="fixed bottom-16 inset-x-0 px-4 sm:px-6 z-20 pointer-events-none">
        <div className="max-w-6xl mx-auto pointer-events-auto bg-card border border-border rounded-[var(--radius)] p-2.5 sm:p-3 flex flex-col sm:flex-row gap-2 shadow-paper-hover">
          <ScanCTA testId="inventory-scan-button" onChip={onChip} label="Escanear chip" />
          <Sheet open={listOpen} onOpenChange={setListOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="xl" data-testid="inventory-list-button"><ClipboardList size={16}/>Lista</Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-[var(--radius)]">
              <SheetHeader><SheetTitle className="font-heading">Pegar lista de chips</SheetTitle></SheetHeader>
              <div className="py-3 space-y-2.5">
                <Textarea value={listText} onChange={(e) => setListText(e.target.value)} rows={5}
                  placeholder="Pegá la lista — uno por línea, separados por coma, etc…"
                  className="font-mono text-[13px]" data-testid="inventory-list-textarea" />
                <div className="grid grid-cols-2 gap-2.5">
                  <Button type="button" variant="outline" className="h-11" onClick={pasteListFromClipboard} data-testid="inventory-list-paste-clipboard">
                    <Clipboard size={15}/>Pegar del portapapeles
                  </Button>
                  <Button type="button" className="h-11" disabled={listBusy} onClick={() => handleBulkPaste(listText)} data-testid="inventory-list-load">
                    {listBusy ? "Cargando…" : "Cargar lista"}
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <Sheet open={manualOpen} onOpenChange={setManualOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="xl" data-testid="inventory-manual-button"><Pen size={16}/>Manual</Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-[var(--radius)]">
              <SheetHeader><SheetTitle className="font-heading">Ingresar código manual</SheetTitle></SheetHeader>
              <ManualEntry onSubmit={(code)=>{ onChip(code); setManualOpen(false); }} />
            </SheetContent>
          </Sheet>
          <Button onClick={finish} size="xl" className="px-5" data-testid="inventory-close-session-button"><Save size={16}/>Cerrar</Button>
        </div>
      </div>

      <UnknownChipDialog
        open={!!unknownDialogChip}
        chipCode={unknownDialogChip || ""}
        farmId={inv?.farm_id}
        canEdit={canEditChips}
        onOpenChange={(o)=>{ if (!o) setUnknownDialogChip(null); }}
        onLinked={handleChipLinked}
        onCreateNew={handleCreateNewFromChip}
      />
    </div>
  );
}

function Stat({ label, value, tone, testId }) {
  const tones = {
    present: "bg-[hsl(var(--state-present-bg))] text-[hsl(var(--state-present))] border-[hsl(var(--state-present)/0.4)]",
    missing: "bg-[hsl(var(--state-missing-bg))] text-[hsl(var(--state-missing))] border-[hsl(var(--state-missing)/0.4)]",
    unknown: "bg-[hsl(var(--state-unknown-bg))] text-[hsl(var(--state-unknown))] border-[hsl(var(--state-unknown)/0.4)]",
    neutral: "bg-muted text-foreground border-border",
  };
  return (
    <div className={cn("rounded-md border px-2.5 py-1.5", tones[tone] || tones.neutral)} data-testid={testId}>
      <div className="text-[9.5px] font-mono uppercase tracking-[0.12em] opacity-80">{label}</div>
      <div className="font-editorial text-[20px] sm:text-[22px] leading-none tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function ManualEntry({ onSubmit }) {
  const [code, setCode] = useState("");
  return (
    <div className="py-3 space-y-3">
      <Input value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g, ""))} placeholder="Código de chip" className="h-12 font-mono tabular-nums tracking-[0.04em]" data-testid="inventory-manual-input" />
      <Button onClick={()=>{ if (code.length >= 6) { onSubmit(code); setCode(""); } }} size="lg" className="w-full" data-testid="inventory-manual-submit">Registrar lectura</Button>
    </div>
  );
}
